import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { LogInstanceToken } from '@evorait/pace-logging';
import { CACHE_FIRST_HEADER, cacheSupported } from './service-worker-cache.utils';

@Injectable()
export class ServiceWorkerCacheInterceptor implements HttpInterceptor {
    private readonly log = inject(LogInstanceToken);
    private readonly cacheSupported = cacheSupported();

    public intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        // Only apply cache-first for GET requests that contain the cache-first header, and when Cache API is available
        const tryCacheFirst = req.method === 'GET' && req.headers.has(CACHE_FIRST_HEADER);
        if (!tryCacheFirst || !this.cacheSupported) {
            return next.handle(req);
        }

        // Use full URL incl. params as cache key
        const cacheKey = req.urlWithParams;

        return from(caches.match(cacheKey)).pipe(
            // Try to read cached JSON
            mergeMap(async (response) => {
                if (response?.ok) {
                    // We expect JSON (same as the original service)
                    this.log.info('ServiceWorkerCacheInterceptor: cache hit', {
                        url: req.url,
                        status: response.statusText,
                    });
                    return response.json().then((data) => data as unknown);
                }
                this.log.info('ServiceWorkerCacheInterceptor: cache miss', { url: req.url });
                return undefined;
            }),
            catchError((err) => {
                this.log.error('ServiceWorkerCacheInterceptor: failed to retrieve from cache, falling back to http.', {
                    err,
                    url: cacheKey,
                });
                return of(undefined);
            }),
            switchMap((cachedBody) => {
                if (cachedBody !== undefined) {
                    // Wrap cached JSON in an HttpResponse so HttpClient consumers stay unchanged
                    const httpResponse = new HttpResponse({
                        body: cachedBody,
                        status: 200,
                        url: cacheKey,
                    });

                    this.log.info('ServiceWorkerCacheInterceptor: returning from cache', { url: req.url });
                    return of(httpResponse as HttpEvent<unknown>);
                }

                // No cache hit -> continue with real HTTP call
                return next.handle(req);
            }),
        );
    }
}
